
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

export const usePasswordManagement = (memberId: string) => {
  const { toast } = useToast();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const resetPassword = async () => {
    try {
      // Get the member's email
      const { data: memberData, error: memberError } = await supabase
        .from('organization_members')
        .select('email')
        .eq('id', memberId)
        .single();

      if (memberError || !memberData?.email) {
        toast({
          title: "Error",
          description: "Failed to fetch member data",
          variant: "destructive",
        });
        return false;
      }

      // Send password reset email
      const { error: resetError } = await supabase.functions.invoke('send-password-reset', {
        body: { email: memberData.email }
      });

      if (resetError) {
        console.error('Error sending password reset:', resetError);
        toast({
          title: "Error",
          description: "Failed to send password reset email",
          variant: "destructive",
        });
        return false;
      }

      // Clear password fields
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      
      toast({
        title: "Success",
        description: "Password reset email has been sent",
      });
      
      return true;
    } catch (error) {
      console.error('Error in resetPassword:', error);
      toast({
        title: "Error",
        description: "Failed to initiate password reset",
        variant: "destructive",
      });
      return false;
    }
  };

  const updatePassword = async () => {
    if (!currentPassword) {
      toast({
        title: "Error",
        description: "Current password is required to change password",
        variant: "destructive",
      });
      return false;
    }

    if (!newPassword || newPassword !== confirmPassword) {
      toast({
        title: "Error",
        description: "New passwords do not match",
        variant: "destructive",
      });
      return false;
    }

    try {
      // Get the member's email
      const { data: memberData, error: memberError } = await supabase
        .from('organization_members')
        .select('email')
        .eq('id', memberId)
        .single();

      if (memberError || !memberData?.email) {
        toast({
          title: "Error",
          description: "Failed to fetch member data",
          variant: "destructive",
        });
        return false;
      }

      // Send password reset email
      const { error: resetError } = await supabase.functions.invoke('send-password-reset', {
        body: { email: memberData.email }
      });

      if (resetError) {
        console.error('Error sending password reset:', resetError);
        toast({
          title: "Error",
          description: "Failed to send password reset email",
          variant: "destructive",
        });
        return false;
      }

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      
      toast({
        title: "Success",
        description: "Password reset email has been sent",
      });
      
      return true;
    } catch (error) {
      console.error('Error updating password:', error);
      toast({
        title: "Error",
        description: "Failed to update password",
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    currentPassword,
    newPassword,
    confirmPassword,
    setCurrentPassword,
    setNewPassword,
    setConfirmPassword,
    updatePassword,
    resetPassword,
  };
};
